import type { DiseaseHistoryState } from "./DiseaseHistoryContext";
import type { BreastTestType } from "./breast-testing/options";
import {
  CATEGORY_FLAG_MAP,
  getDiseaseLabelKey,
  getDiseaseSourceCodes,
} from "./diseases/options";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";
import type { TranslationFunction } from "@src/packages/i18n";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type MedicalHistoryPayload = NonNullable<PatchPayload["medicalHistory"]>;
type DiseasePayload = NonNullable<MedicalHistoryPayload["diseases"]>[number];
type MutableEntry = Record<string, unknown>;

/**
 * Map our BreastTestType to API type + category fields.
 */
function breastTestToApiFields(testType: BreastTestType): {
  type: string;
  imagingTestCategory?: string;
  clinicalTestCategory?: string;
} {
  if (testType === "clinical_breast_exam") {
    return { type: "clinical", clinicalTestCategory: "breast_examination" };
  }
  // All others are imaging (including "other")
  return { type: "imaging", imagingTestCategory: testType };
}

export function transformDiseaseHistoryStateToRequest(
  state: DiseaseHistoryState,
  t: TranslationFunction,
): PatchPayload {
  type BreastConditionEntry = {
    id?: string;
    condition: "discharge" | "pain" | "other";
    other?: string | null;
    op?: "delete";
  };

  const medicalHistory: MedicalHistoryPayload = {};

  // ── Breast Testings (boolean flag) ─────────────────────
  if (state.hasBreastTestings !== null) {
    medicalHistory.hasBreastTestings = state.hasBreastTestings;
  }

  // ── Breast Conditions (boolean flag) ───────────────────
  if (state.hasBreastConditions !== null) {
    medicalHistory.hasBreastConditions = state.hasBreastConditions;
  }

  // ── Disease Category Flags ─────────────────────────────
  for (const [category, flagName] of Object.entries(CATEGORY_FLAG_MAP)) {
    const value =
      state.diseaseCategoryFlags[
        category as keyof typeof state.diseaseCategoryFlags
      ];
    if (value !== null) {
      (medicalHistory as MutableEntry)[flagName] = value;
    }
  }

  // ── Cancer History Flag ────────────────────────────────
  if (state.hasCancerHistory !== null) {
    medicalHistory.hasCancerHistory = state.hasCancerHistory;
  }

  // ── Breast Condition entries ───────────────────────────
  const breastConditionsPayload: BreastConditionEntry[] = [];

  // Handle deletions
  state.breastConditionIdsToDelete.forEach((id) => {
    if (id) {
      breastConditionsPayload.push({
        id,
        condition: "other",
        op: "delete",
      });
    }
  });

  if (state.hasBreastConditions === true) {
    const deleteIdSet = new Set(state.breastConditionIdsToDelete);
    state.breastConditions
      .filter((c) => !c.id || !deleteIdSet.has(c.id))
      .forEach((c) => {
        const entry: BreastConditionEntry = {
          condition: c.condition,
        };
        if (c.id) entry.id = c.id;
        if (c.other != null) entry.other = c.other;
        breastConditionsPayload.push(entry);
      });
  } else if (state.hasBreastConditions === false) {
    // Delete all existing conditions when user says "none"
    const deleteIdSet = new Set(state.breastConditionIdsToDelete);
    state.breastConditions.forEach((c) => {
      if (c.id && !deleteIdSet.has(c.id)) {
        breastConditionsPayload.push({
          id: c.id,
          condition: c.condition,
          op: "delete",
        });
      }
    });
  }

  if (breastConditionsPayload.length > 0) {
    medicalHistory.breastConditions = breastConditionsPayload;
  }

  // ── Disease entries ────────────────────────────────────
  const diseasesPayload: MutableEntry[] = [];

  // Handle deletions
  const diseaseDeleteIdSet = new Set(state.diseaseIdsToDelete);
  state.diseaseIdsToDelete.forEach((id) => {
    if (id) {
      diseasesPayload.push({ id, op: "delete" });
    }
  });

  // Add active disease entries
  state.diseases
    .filter(
      (d) =>
        state.diseaseCategoryFlags[d.category] !== false &&
        (!d.id || !diseaseDeleteIdSet.has(d.id)),
    )
    .forEach((d) => {
      const sourceCodes = getDiseaseSourceCodes(
        d.category,
        d.disease,
        t(getDiseaseLabelKey(d.category, d.disease)),
      );
      const entry = {
        category: d.category,
        disease: d.disease,
        ...(d.id ? { id: d.id } : {}),
        ...(d.other != null ? { other: d.other } : {}),
        ...(d.diagnosisDate != null ? { diagnosisDate: d.diagnosisDate } : {}),
        ...(d.treatment != null ? { treatmentDescription: d.treatment } : {}),
        ...(sourceCodes.length > 0 ? { sourceCodes } : {}),
      } satisfies DiseasePayload;
      diseasesPayload.push(entry);
    });

  // Delete diseases for categories set to false
  for (const [category, value] of Object.entries(state.diseaseCategoryFlags)) {
    if (value === false) {
      state.diseases
        .filter(
          (d) =>
            d.category === category && d.id && !diseaseDeleteIdSet.has(d.id),
        )
        .forEach((d) => {
          diseasesPayload.push({ id: d.id, op: "delete" });
        });
    }
  }

  if (diseasesPayload.length > 0) {
    medicalHistory.diseases = diseasesPayload as NonNullable<
      MedicalHistoryPayload["diseases"]
    >;
  }

  // ── Cancer entries ─────────────────────────────────────
  const cancersPayload: MutableEntry[] = [];

  // Handle deletions
  const cancerDeleteIdSet = new Set(state.cancerIdsToDelete);
  state.cancerIdsToDelete.forEach((id) => {
    if (id) {
      cancersPayload.push({ id, op: "delete" });
    }
  });

  if (state.hasCancerHistory === true) {
    state.cancers
      .filter((c) => !c.id || !cancerDeleteIdSet.has(c.id))
      .forEach((c) => {
        const treatments: MutableEntry[] = [];

        // Handle treatment deletions
        c.treatmentIdsToDelete.forEach((id) => {
          if (id) {
            treatments.push({ id, op: "delete" });
          }
        });

        const treatmentDeleteIdSet = new Set(c.treatmentIdsToDelete);
        c.treatments
          .filter((t) => !t.id || !treatmentDeleteIdSet.has(t.id))
          .forEach((t) => {
            const entry: MutableEntry = {
              treatmentType: t.treatmentType,
            };
            if (t.id) entry.id = t.id;
            if (t.other != null) entry.other = t.other;
            if (t.startDate != null) entry.startDate = t.startDate;
            if (t.endDate != null) entry.endDate = t.endDate;
            if (t.notes != null) entry.notes = t.notes;
            treatments.push(entry);
          });

        const cancerEntry: MutableEntry = {};
        if (c.id) cancerEntry.id = c.id;
        if (c.cancer != null) cancerEntry.cancer = c.cancer;
        if (c.diagnosisDate != null) {
          cancerEntry.diagnosisDate = c.diagnosisDate;
        }
        if (c.inRemission != null) cancerEntry.inRemission = c.inRemission;
        if (treatments.length > 0) cancerEntry.treatments = treatments;
        cancersPayload.push(cancerEntry);
      });
  } else if (state.hasCancerHistory === false) {
    // Delete all existing cancers when user says "no"
    state.cancers.forEach((c) => {
      if (c.id && !cancerDeleteIdSet.has(c.id)) {
        cancersPayload.push({ id: c.id, op: "delete" });
      }
    });
  }

  if (cancersPayload.length > 0) {
    medicalHistory.cancers = cancersPayload as NonNullable<
      MedicalHistoryPayload["cancers"]
    >;
  }

  // ── Breast Test entries (saved as testResults) ─────────
  const testResults: MutableEntry[] = [];

  // Handle deletions
  const testDeleteIdSet = new Set(state.breastTestingIdsToDelete);
  state.breastTestingIdsToDelete.forEach((id) => {
    if (id) {
      testResults.push({ id, op: "delete" });
    }
  });

  if (state.hasBreastTestings === true) {
    state.breastTestings
      .filter((t) => !t.id || !testDeleteIdSet.has(t.id))
      .forEach((t) => {
        const apiFields = breastTestToApiFields(t.testType);
        const entry: MutableEntry = { ...apiFields };
        if (t.id) entry.id = t.id;
        if (t.other != null) entry.name = t.other;
        if (t.testDate != null) entry.date = t.testDate;
        if (t.outcome != null) entry.patientValue = t.outcome;
        if (t.notes != null) entry.notes = t.notes;
        testResults.push(entry);
      });
  } else if (state.hasBreastTestings === false) {
    // Delete all existing test entries when user says "none"
    state.breastTestings.forEach((t) => {
      if (t.id && !testDeleteIdSet.has(t.id)) {
        testResults.push({ id: t.id, op: "delete" });
      }
    });
  }

  // ── Build payload ──────────────────────────────────────
  const payload: PatchPayload = {};

  if (Object.keys(medicalHistory).length > 0) {
    payload.medicalHistory = medicalHistory;
  }

  if (testResults.length > 0) {
    payload.testResults = testResults as NonNullable<
      PatchPayload["testResults"]
    >;
  }

  return payload;
}
